import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import contentsData from '../../assets/contents_alab.json';
import globalContentsData from '../../assets/contents_global_alab.json';
import collaboLogo from '../../assets/images/collabo_logo.png';
import instagramIcon from '../../assets/icons/instagram.svg';
import youtubeIcon from '../../assets/icons/youtube.svg';
import tiktokIcon from '../../assets/icons/titok.svg';
import moveArrowIcon from '../../assets/icons/move_arrow.svg';
import {
  ContentCardThumbnail,
  isYoutubePlatform,
} from '../section/ContentCardThumbnail';
import {
  PillButton,
  Reveal,
  Section,
  SectionInner,
  SectionLabel,
  SectionTitle,
} from '../section/common';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import {
  getContentThumbnailUrl,
  openExternalLink,
  pickRandomItems,
  type ContentItem,
} from '../../utils/listingData';

const CARD_WIDTH = 400;
const CARD_GAP = 16;
const CAROUSEL_TRANSITION_MS = 400;
const VISIBLE_ITEM_COUNT = 20;
const ARROW_BUTTON_SIZE = 80;
const CAROUSEL_VIEWPORT_WIDTH = 1240;

const PLATFORM_META: Record<string, { label: string; icon: string }> = {
  YOUTUBE: { label: 'Youtube', icon: youtubeIcon },
  INSTAGRAM: { label: 'Instagram', icon: instagramIcon },
  TIKTOK: { label: 'TikTok', icon: tiktokIcon },
};

const ContentsWrapper = styled(Section)`
  padding: 80px 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 30px 0;
  }
`;

const HeaderBlock = styled.div`
  text-align: center;
  margin-bottom: 48px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 32px;
  }
`;

const CollaboLogo = styled.img`
  height: 200px;
  width: auto;
  margin: 32px auto 60px;
  display: block;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 126px;
  }
`;

const Description = styled.p`
  color: #555;
  text-align: center;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%; /* 28px */
  letter-spacing: -0.5px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 15px;
    letter-spacing: -0.38px;
  }
`;

const DescriptionGreen = styled.span`
  color: #16a96d;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: -0.5px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 15px;
  }
`;

const TabGroup = styled.div`
  display: flex;
  justify-content: center;
  margin: 40px 0 32px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin: 24px 0;
  }
`;

const TabGroupTrack = styled.div`
  display: inline-grid;
  grid-template-columns: repeat(2, 1fr);
  padding: 4px;
  border-radius: 999px;
  background: #eeeeee;
  border: 1px solid #ffffff;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 32px;
  border-radius: 999px;
  border: none;
  background: ${({ $active }) => ($active ? '#111111' : 'transparent')};
  color: ${({ $active }) => ($active ? '#CEDA87' : '#555555')};
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
`;

const CarouselFullBleed = styled.div`
  width: 100vw;
  margin-left: calc(50% - 50vw);
  position: relative;
`;

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const CarouselViewport = styled.div`
  width: 100%;
  overflow: hidden;
  touch-action: pan-y pinch-zoom;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0 20px;
  }
`;

const CarouselSlider = styled.div<{
  $offset: number;
  $animate: boolean;
  $gap: number;
}>`
  display: flex;
  gap: ${({ $gap }) => $gap}px;
  transform: translateX(${({ $offset }) => $offset}px);
  transition: ${({ $animate }) =>
    $animate ? `transform ${CAROUSEL_TRANSITION_MS}ms ease` : 'none'};
  will-change: transform;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const CreatorName = styled.p`
  overflow: hidden;
  height: 96px;
  color: #fff;
  text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 40px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -1px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 72px;
    font-size: 28px;
    letter-spacing: -0.7px;
  }
`;

const CardArrowCircle = styled.div`
  display: flex;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.lime};
  transition: background 0.25s ease;
`;

const CardArrow = styled.img`
  width: 24px;
  height: 24px;
  filter: brightness(0);
  transition: filter 0.25s ease;
`;

const ContentCard = styled.a<{ $cardWidth: number }>`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 0 0 ${({ $cardWidth }) => $cardWidth}px;
  width: ${({ $cardWidth }) => $cardWidth}px;
  height: 635px;
  padding: 24px 20px 20px;
  border-radius: 28px;
  background: #111111;
  text-decoration: none;
  color: inherit;
  transition: background 0.25s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 520px;
    padding: 20px 16px 16px;
    border-radius: 24px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.lime};

    ${CreatorName} {
      color: #111111;
    }

    ${CardArrowCircle} {
      background: #111111;
    }

    ${CardArrow} {
      filter: none;
    }
  }
`;

const CardMedia = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  min-height: 0;
  border-radius: 20px;
  overflow: hidden;
  background: #000000;
`;

const PlatformBadge = styled.div`
  position: absolute;
  left: 12px;
  bottom: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
`;

const PlatformIcon = styled.img`
  width: 16px;
  height: 16px;
`;

const PlatformName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #ffffff;
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 80px;
  height: 80px;
  border-radius: 80px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.18);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.28);
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
`;

const CarouselArrowIcon = styled.svg`
  width: 12px;
  height: 22px;
  flex-shrink: 0;

  path {
    fill: rgba(255, 255, 255, 0.1);
    stroke: #ffffff;
    stroke-width: 2px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 10px;
    height: 18px;

    path {
      fill: none;
      stroke: #ffffff;
      stroke-width: 1.75px;
    }
  }
`;

const PrevButton = styled(ArrowButton)`
  left: max(
    24px,
    calc(50% - ${CAROUSEL_VIEWPORT_WIDTH / 2 + ARROW_BUTTON_SIZE}px)
  );

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    left: 12px;
  }
`;

const NextButton = styled(ArrowButton)`
  right: max(
    24px,
    calc(50% - ${CAROUSEL_VIEWPORT_WIDTH / 2 + ARROW_BUTTON_SIZE}px)
  );

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    right: 12px;
  }
`;

const MoreButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
`;

const formatCreatorName = (name: string) =>
  name.startsWith('@') ? name : `@${name}`;

const getPlatformMeta = (platform: string) =>
  PLATFORM_META[platform.toUpperCase()] ?? {
    label: platform,
    icon: instagramIcon,
  };

interface ContentCarouselProps {
  items: ContentItem[];
}

const SWIPE_THRESHOLD = 50;

const ContentCarousel: React.FC<ContentCarouselProps> = ({ items }) => {
  const [cardWidth, setCardWidth] = useState(CARD_WIDTH);
  const [activeIndex, setActiveIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const isMovingRef = useRef(false);
  const touchStart = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const updateCardWidth = () => {
      if (window.matchMedia('(max-width: 768px)').matches) {
        setCardWidth(Math.min(CARD_WIDTH, window.innerWidth - 40));
      } else {
        setCardWidth(CARD_WIDTH);
      }
    };

    updateCardWidth();
    window.addEventListener('resize', updateCardWidth);
    return () => window.removeEventListener('resize', updateCardWidth);
  }, []);

  const cardGap = CARD_GAP;
  const cardStep = cardWidth + cardGap;
  const total = items.length;
  const loopItems = useMemo(() => [...items, ...items, ...items], [items]);
  const offset = -(total + activeIndex) * cardStep;

  const moveBy = useCallback(
    (direction: 'left' | 'right') => {
      if (total === 0 || isMovingRef.current) return;

      isMovingRef.current = true;
      setAnimate(true);
      setActiveIndex((prev) => prev + (direction === 'left' ? -1 : 1));

      window.setTimeout(() => {
        isMovingRef.current = false;
      }, CAROUSEL_TRANSITION_MS);
    },
    [total],
  );

  useEffect(() => {
    if (total === 0) return;

    if (activeIndex >= total || activeIndex < 0) {
      const timer = window.setTimeout(() => {
        setAnimate(false);
        setActiveIndex((prev) => {
          if (prev >= total) return 0;
          if (prev < 0) return total - 1;
          return prev;
        });
      }, CAROUSEL_TRANSITION_MS);

      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, [activeIndex, total]);

  useEffect(() => {
    if (!animate) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimate(true));
      });
    }
  }, [animate]);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStart.current = {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY,
    };
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    const touch = event.changedTouches[0];
    const dx = touchStart.current.x - touch.clientX;
    const dy = touchStart.current.y - touch.clientY;

    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
    moveBy(dx > 0 ? 'right' : 'left');
  };

  return (
    <CarouselWrapper>
      <PrevButton
        type="button"
        onClick={() => moveBy('left')}
        aria-label="이전"
      >
        <CarouselArrowIcon viewBox="0 0 15 24" aria-hidden>
          <path d="M13.4799 0.737061L1.47992 11.7371L13.4799 22.7371" />
        </CarouselArrowIcon>
      </PrevButton>

      <CarouselViewport
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <CarouselSlider $offset={offset} $animate={animate} $gap={cardGap}>
          {loopItems.map((item, index) => {
            const platform = getPlatformMeta(item.platform);
            const isYoutube = isYoutubePlatform(item.platform);

            return (
              <ContentCard
                key={`${item.content_url}-${index}`}
                $cardWidth={cardWidth}
                href={item.content_url || undefined}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
                  openExternalLink(event, item.content_url)
                }
              >
                <CardHeader>
                  <CreatorName>{formatCreatorName(item.name)}</CreatorName>
                  <CardArrowCircle aria-hidden>
                    <CardArrow src={moveArrowIcon} alt="" />
                  </CardArrowCircle>
                </CardHeader>
                <CardMedia>
                  <ContentCardThumbnail
                    $isYoutube={isYoutube}
                    src={getContentThumbnailUrl(item)}
                    alt={item.name}
                    loading="lazy"
                  />
                  <PlatformBadge>
                    <PlatformIcon src={platform.icon} alt="" />
                    <PlatformName>{platform.label}</PlatformName>
                  </PlatformBadge>
                </CardMedia>
              </ContentCard>
            );
          })}
        </CarouselSlider>
      </CarouselViewport>

      <NextButton
        type="button"
        onClick={() => moveBy('right')}
        aria-label="다음"
      >
        <CarouselArrowIcon viewBox="0 0 15 24" aria-hidden>
          <path d="M0.675781 0.737061L12.6758 11.7371L0.675781 22.7371" />
        </CarouselArrowIcon>
      </NextButton>
    </CarouselWrapper>
  );
};

const ContentsSection: React.FC = () => {
  const { ref, isVisible } = useScrollReveal();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'korea' | 'global'>('korea');
  const koreaItems = contentsData as ContentItem[];
  const globalItems = globalContentsData as ContentItem[];

  const visibleItems = useMemo(
    () =>
      pickRandomItems(
        activeTab === 'korea' ? koreaItems : globalItems,
        VISIBLE_ITEM_COUNT,
      ),
    [activeTab, koreaItems, globalItems],
  );

  return (
    <ContentsWrapper id="contents">
      <SectionInner ref={ref}>
        <Reveal $visible={isVisible}>
          <HeaderBlock>
            <SectionLabel>CONTENTS</SectionLabel>
            <SectionTitle>{`같이 만들수록 커지는 가치`}</SectionTitle>
            <Description>
              브랜드의 이야기가 더 멀리 닿을 수 있도록,
              <br />
              <DescriptionGreen>아보카도자이언트</DescriptionGreen>와 함께
              콘텐츠를 기획하고 제작합니다.
            </Description>
            <CollaboLogo src={collaboLogo} alt="LALAFAN x AVOCADO GIANT" />
          </HeaderBlock>
        </Reveal>

        <TabGroup>
          <TabGroupTrack>
            <Tab
              $active={activeTab === 'korea'}
              type="button"
              onClick={() => setActiveTab('korea')}
            >
              Korea
            </Tab>
            <Tab
              $active={activeTab === 'global'}
              type="button"
              onClick={() => setActiveTab('global')}
            >
              Global
            </Tab>
          </TabGroupTrack>
        </TabGroup>
      </SectionInner>

      <CarouselFullBleed>
        <ContentCarousel key={activeTab} items={visibleItems} />
      </CarouselFullBleed>

      <SectionInner>
        <MoreButtonWrapper>
          <PillButton type="button" onClick={() => navigate('/contents')}>
            콘텐츠 더보기 →
          </PillButton>
        </MoreButtonWrapper>
      </SectionInner>
    </ContentsWrapper>
  );
};

export default ContentsSection;
